import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, IconButton, TablePagination, LinearProgress,
  Tooltip, Menu, MenuItem,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import { toast } from 'react-toastify';
import { instance } from '../../axios';
import {
  getCompaniesAction,
  cleanCompanyAction,
  getCompanyAction,
} from '../../redux/actions/company';
import { updateSelectedCompanyAction } from '../../redux/actions/login';
import { updateUserAction } from '../../redux/actions/user';
import strings from '../../i18n/language';
import CompanyDelete from '../../dialog/CompanyDelete';
import CompanyEdit from './Edit';
import CompanyReport from './Report';
import ReactCountryFlag from 'react-country-flag';

const BRAND = '#3b95e8';
const GLASS = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.07)';
const MUTED = 'rgba(232,240,254,0.35)';
const ROW_PER_PAGE = 10;

const strategyMeta = {
  standard: { label: 'Standard', color: 'rgba(59,149,232,0.15)', text: '#6ab0f0' },
  plus:     { label: 'Fidelize+', color: 'rgba(91,191,181,0.15)', text: '#5bbfb5' },
};

// Languages the API renders the team guide in, the shop's country one first
const GUIDE_LANGUAGES = [
  { key: 'es', label: 'Español' },
  { key: 'fr', label: 'Français' },
  { key: 'en', label: 'English' },
  { key: 'it', label: 'Italiano' },
];
const COUNTRY_LANGUAGE = { FR: 'fr', IT: 'it' };

const guideLanguagesFor = (company) => {
  const first = COUNTRY_LANGUAGE[company?.Country?.key] || 'es';
  return [
    ...GUIDE_LANGUAGES.filter((language) => language.key === first),
    ...GUIDE_LANGUAGES.filter((language) => language.key !== first),
  ];
};

// What the API reports missing when a shop is not ready for its guide
const GUIDE_MISSING_LABELS = {
  requirement: 'LABEL_COMPANIES_GUIDE_MISSING_REQUIREMENT',
  gifts: 'LABEL_COMPANIES_GUIDE_MISSING_GIFTS',
};

const Badge = ({ value }) => {
  const meta = strategyMeta[value] || { label: value || '—', color: 'rgba(255,255,255,0.06)', text: MUTED };
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 0.3,
      background: meta.color,
      color: meta.text,
    }}>
      {meta.label}
    </span>
  );
};

const ColHeader = ({ children }) => (
  <th style={{
    padding: '10px 16px',
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: MUTED,
    borderBottom: `1px solid ${BORDER}`,
    whiteSpace: 'nowrap',
  }}>
    {children}
  </th>
);

const CompanyList = () => {
  const companies = useSelector((state) => state.company.companies);
  const [companyDeleteId, setCompanyDeleteId] = useState(null);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [page, setPage] = useState(0);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [guideMenu, setGuideMenu] = useState(null);
  const [reportCompany, setReportCompany] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector((state) => state.company.isLoadingGetCompanies);
  const user = useSelector((state) => state.user.user);
  const total = useSelector((state) => state.company.total);

  useEffect(() => {
    dispatch(getCompaniesAction(ROW_PER_PAGE, 0));
  }, [dispatch]);

  // The guide route needs the Authorization header, so the page is fetched
  // here and shown from memory: opening it by URL would need ?token=, which
  // the API writes to its logs
  const openGuide = async (company, lang) => {
    // Keeps the company so the menu does not reorder while it fades out
    setGuideMenu((menu) => ({ ...menu, anchor: null }));
    // Opened on the click itself: a window opened once the answer is back
    // would be taken for a popup and blocked
    const guideWindow = window.open('', '_blank');
    try {
      const response = await instance.get(`/company/${company.id}/guide`, { params: { lang } });
      const page = URL.createObjectURL(new Blob([response.data], { type: 'text/html' }));
      guideWindow.location.href = page;
      setTimeout(() => URL.revokeObjectURL(page), 60000);
    } catch (error) {
      guideWindow?.close();
      const missing = error.response?.status === 409 ? error.response.data?.missing : null;
      if (missing?.length) {
        const labels = missing.map((field) => strings[GUIDE_MISSING_LABELS[field]] || field);
        toast.error(strings.formatString(strings.LABEL_COMPANIES_GUIDE_INCOMPLETE, labels.join(', ')));
      } else {
        toast.error(strings.LABEL_COMPANIES_GUIDE_ERROR);
      }
    }
  };

  return (
    <Box sx={{ width: "100%" }}>

      {/* Card */}
      <Box sx={{
        background: GLASS,
        border: `1px solid ${BORDER}`,
        borderRadius: 3,
        backdropFilter: 'blur(12px)',
        overflow: 'hidden',
      }}>

        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 3, py: 2.5,
          borderBottom: `1px solid ${BORDER}`,
        }}>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe', letterSpacing: 0.2 }}>
              Empresas
            </Typography>
            {total > 0 && (
              <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.3 }}>
                {total} {total === 1 ? 'empresa' : 'empresas'}
              </Typography>
            )}
          </Box>
          <button
            onClick={() => { dispatch(cleanCompanyAction()); setOpenModalEdit(true); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: BRAND,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(59,149,232,0.3)',
              letterSpacing: 0.2,
            }}>
            <AddRoundedIcon style={{ fontSize: 16 }} />
            Nueva empresa
          </button>
        </Box>

        {/* Loading */}
        {(isLoading || !companies) && (
          <LinearProgress sx={{ height: 2, '& .MuiLinearProgress-bar': { background: BRAND } }} />
        )}

        {/* Table */}
        {companies && (
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <ColHeader>ID</ColHeader>
                  <ColHeader>{strings.LABEL_COMPANIES_HEADER_COUNTRY}</ColHeader>
                  <ColHeader>{strings.LABEL_COMPANIES_HEADER_NAME}</ColHeader>
                  <ColHeader>Tipo</ColHeader>
                  <ColHeader>Código</ColHeader>
                  <ColHeader>Estrategia</ColHeader>
                  <ColHeader>{strings.LABEL_COMPANIES_HEADER_ACTION}</ColHeader>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr
                    key={company.id}
                    onMouseEnter={() => setHoveredRow(company.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      background: hoveredRow === company.id ? 'rgba(255,255,255,0.03)' : 'transparent',
                      transition: 'background 0.15s',
                    }}
                  >
                    <td style={{ padding: '12px 16px', borderBottom: `1px solid ${BORDER}`, color: MUTED, fontSize: 13 }}>
                      {company.id}
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: `1px solid ${BORDER}` }}>
                      <ReactCountryFlag countryCode={company.Country?.key} svg
                        style={{ width: '1.4em', height: '1.4em', borderRadius: 2 }}
                        title={company.Country?.key}
                      />
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: `1px solid ${BORDER}`, color: '#e8f0fe', fontSize: 13, fontWeight: 500 }}>
                      {company.name}
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: `1px solid ${BORDER}`, color: MUTED, fontSize: 13 }}>
                      {company.public ? 'Public' : 'Private'}
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: `1px solid ${BORDER}`, color: MUTED, fontSize: 13, fontFamily: 'monospace' }}>
                      {company.code || '—'}
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: `1px solid ${BORDER}` }}>
                      <Badge value={company.strategy} />
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: `1px solid ${BORDER}` }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Conectarse" placement="top">
                          <IconButton size="small" sx={{ color: 'rgba(232,240,254,0.4)', '&:hover': { color: BRAND, background: 'rgba(59,149,232,0.1)' } }}
                            onClick={() => {
                              dispatch(updateSelectedCompanyAction(company));
                              dispatch(updateUserAction({ ...user, company_id: company.id }));
                              navigate('/');
                            }}>
                            <LoginRoundedIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar" placement="top">
                          <IconButton size="small" sx={{ color: 'rgba(232,240,254,0.4)', '&:hover': { color: '#e8f0fe', background: 'rgba(255,255,255,0.06)' } }}
                            onClick={() => { dispatch(getCompanyAction(company.id)); setOpenModalEdit(true); }}>
                            <EditRoundedIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={strings.LABEL_COMPANIES_GUIDE} placement="top">
                          <IconButton size="small" sx={{ color: 'rgba(232,240,254,0.4)', '&:hover': { color: BRAND, background: 'rgba(59,149,232,0.1)' } }}
                            onClick={(event) => setGuideMenu({ anchor: event.currentTarget, company })}>
                            <MenuBookRoundedIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={strings.LABEL_COMPANIES_REPORT} placement="top">
                          <IconButton size="small" sx={{ color: 'rgba(232,240,254,0.4)', '&:hover': { color: BRAND, background: 'rgba(59,149,232,0.1)' } }}
                            onClick={() => setReportCompany(company)}>
                            <AssessmentRoundedIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar" placement="top">
                          <IconButton size="small" sx={{ color: 'rgba(232,240,254,0.4)', '&:hover': { color: '#e05c5c', background: 'rgba(224,92,92,0.1)' } }}
                            onClick={() => { setCompanyDeleteId(company.id); setOpenModalDelete(true); }}>
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <TablePagination
              rowsPerPageOptions={[ROW_PER_PAGE]}
              count={total}
              rowsPerPage={ROW_PER_PAGE}
              page={page}
              onPageChange={(_, newPage) => {
                setPage(newPage);
                dispatch(getCompaniesAction(ROW_PER_PAGE, newPage * ROW_PER_PAGE));
              }}
              component="div"
              sx={{
                color: MUTED,
                fontSize: 12,
                borderTop: `1px solid ${BORDER}`,
                '& .MuiIconButton-root': { color: MUTED },
                '& .MuiTablePagination-displayedRows': { fontSize: 12 },
              }}
            />
          </Box>
        )}
      </Box>

      <Menu
        anchorEl={guideMenu?.anchor}
        open={Boolean(guideMenu?.anchor)}
        onClose={() => setGuideMenu((menu) => ({ ...menu, anchor: null }))}
      >
        {guideLanguagesFor(guideMenu?.company).map((language) => (
          <MenuItem key={language.key} sx={{ fontSize: 13 }}
            onClick={() => openGuide(guideMenu.company, language.key)}>
            {language.label}
          </MenuItem>
        ))}
      </Menu>

      <CompanyReport company={reportCompany} onClose={() => setReportCompany(null)} />

      <CompanyDelete open={openModalDelete} setOpen={setOpenModalDelete} companyDeleteId={companyDeleteId} />
      <CompanyEdit open={openModalEdit} onClose={() => setOpenModalEdit(false)} />
    </Box>
  );
};

export default CompanyList;
